import { useState, useEffect, useRef } from 'react';
import type { Deal, TabState } from '../../types';
import { getFiltered } from '../../utils/filter';
import { fmtG } from '../../utils/format';
import { dedupeById, sumUniqueBy, isAged, ageSeverity, formatAge, splitSCNames } from '../../utils/deals';
import { MetricCard } from '../ui/MetricCard';
import { GnarrInfluencedCard } from '../ui/GnarrInfluencedCard';
import { SectionHeader } from '../ui/SectionHeader';
import { RangeSlider } from '../ui/RangeSlider';
import { OfferingChart, bucketSolution } from '../charts/OfferingChart';
import { SortableTable, type SortableColumn } from '../ui/SortableTable';
import { useDeals } from '../../context/DealsContext';
import React from 'react';
interface Props {
  state: TabState;
  searchRows?: React.ReactNode;
}
function regionBadge(region: string) {
  const styles: Record<string, {
    bg: string;
    color: string;
  }> = {
    AMER: {
      bg: '#EFF6FF',
      color: '#1D4ED8'
    },
    EMEA: {
      bg: '#F5F3FF',
      color: '#7C3AED'
    },
    JAPAC: {
      bg: '#F0FDF4',
      color: '#15803D'
    }
  };
  const s = styles[region] || {
    bg: '#F9FAFB',
    color: '#374151'
  };
  return <span className="badge" style={{
    background: s.bg,
    color: s.color,
    fontSize: 8
  }} data-id="element-592" data-mp="120jumlynzoy3">{region}</span>;
}
export function SDPanel({
  state,
  searchRows
}: Props) {
  const {
    deals
  } = useDeals();
  const all = getFiltered('sd', state, deals);
  const ip = all.filter(d => d.status === 'In Progress');
  const cls = all.filter(d => d.status === 'Closed');
  const aged = ip.filter(d => isAged(d));
  const scs = new Set(ip.flatMap(d => splitSCNames(d.sc))).size;
  const oppGnarr = sumUniqueBy(all, d => d.opp_gnarr);

  // Closed Booked GNARR: won deals matching region/sub and close_q quarter filter
  const wonDeals = dedupeById(deals.filter(d => {
    if (d.adjusted_commit !== 'Won') return false;
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    if (state.q !== 'All' || state.fy !== 'All') {
      if (!d.close_q) return false;
      const [yr, qt] = d.close_q.split('-');
      if (state.fy !== 'All' && yr !== state.fy) return false;
      if (state.q !== 'All' && qt !== state.q) return false;
    }
    return true;
  }));
  const wonGnarr = wonDeals.reduce((s, d) => s + (d.opp_gnarr || 0), 0);
  const wonCount = wonDeals.length;
  const agedTop8 = aged.slice(0, 8);

  // Merge deals by id: combine SC names, keep first occurrence's other fields
  interface MergedDeal extends Deal {
    mergedSc: string;
    displayStatus: string;
  }
  const dealMap = new Map<string, MergedDeal>();
  for (const d of all) {
    const existing = dealMap.get(d.id);
    if (existing) {
      const scNames = new Set([...existing.mergedSc.split(',').map(s => s.trim()).filter(Boolean), ...d.sc.split(',').map(s => s.trim()).filter(Boolean)]);
      existing.mergedSc = [...scNames].join(', ');
      if (!existing.displayStatus && d.opp_status) existing.displayStatus = d.opp_status;
    } else {
      dealMap.set(d.id, {
        ...d,
        mergedSc: d.sc,
        displayStatus: d.opp_status
      });
    }
  }
  const mergedDeals = [...dealMap.values()];
  const [searchId, setSearchId] = useState('');
  const [searchAccount, setSearchAccount] = useState('');
  const [searchSc, setSearchSc] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [regionDropdownOpen, setRegionDropdownOpen] = useState(false);
  const regionDropdownRef = useRef<HTMLDivElement>(null);
  const [selectedSubs, setSelectedSubs] = useState<string[]>([]);
  const [subDropdownOpen, setSubDropdownOpen] = useState(false);
  const subDropdownRef = useRef<HTMLDivElement>(null);

  // Derived ranges from data
  const ages = mergedDeals.map(d => d.age ?? 0);
  const gnarrs = mergedDeals.map(d => d.gnarr ?? 0);
  const dataAgeMin = Math.min(...ages);
  const dataAgeMax = Math.max(...ages);
  const dataGnarrMin = Math.min(...gnarrs);
  const dataGnarrMax = Math.max(...gnarrs);
  const [ageMin, setAgeMin] = useState(dataAgeMin);
  const [ageMax, setAgeMax] = useState(dataAgeMax);
  const [gnarrMin, setGnarrMin] = useState(dataGnarrMin);
  const [gnarrMax, setGnarrMax] = useState(dataGnarrMax);
  const [selectedCloseQs, setSelectedCloseQs] = useState<string[]>([]);
  const [closeQDropdownOpen, setCloseQDropdownOpen] = useState(false);
  const closeQDropdownRef = useRef<HTMLDivElement>(null);
  const [offeringTop, setOfferingTop] = useState(8);
  const [offeringTotal, setOfferingTotal] = useState(1);
  const [selectedSolution, setSelectedSolution] = useState<string | null>(null);
  const [othersLabels, setOthersLabels] = useState<string[]>([]);
  const handleSolutionClick = (name: string) => {
    setSelectedSolution(prev => prev === name ? null : name);
  };
  useEffect(() => {
    if (!statusDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target as Node)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [statusDropdownOpen]);
  useEffect(() => {
    if (!regionDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (regionDropdownRef.current && !regionDropdownRef.current.contains(e.target as Node)) {
        setRegionDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [regionDropdownOpen]);
  useEffect(() => {
    if (!subDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (subDropdownRef.current && !subDropdownRef.current.contains(e.target as Node)) {
        setSubDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [subDropdownOpen]);
  useEffect(() => {
    if (!closeQDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (closeQDropdownRef.current && !closeQDropdownRef.current.contains(e.target as Node)) {
        setCloseQDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [closeQDropdownOpen]);
  const allStatuses = [...new Set(mergedDeals.map(d => d.displayStatus).filter(Boolean))].sort();
  const allRegions = [...new Set(mergedDeals.map(d => d.region).filter(Boolean))].sort();

  // Build region ↔ sub mappings from data
  const regionToSubs = new Map<string, Set<string>>();
  const subToRegion = new Map<string, string>();
  for (const d of mergedDeals) {
    if (!d.region || !d.sub_region) continue;
    if (!regionToSubs.has(d.region)) regionToSubs.set(d.region, new Set());
    regionToSubs.get(d.region)!.add(d.sub_region);
    if (!subToRegion.has(d.sub_region)) subToRegion.set(d.sub_region, d.region);
  }

  // Subs available: if regions are selected, only show subs under those regions; otherwise all
  const availableSubs = selectedRegions.length > 0 ? [...new Set(selectedRegions.flatMap(r => [...(regionToSubs.get(r) ?? [])]))].sort() : [...new Set(mergedDeals.map(d => d.sub_region).filter(Boolean))].sort();

  // Auto-select region when a sub is selected and no region is active;
  // auto-clear region when deselecting a sub leaves no subs under that region.
  const autoSelectedRegionRef = useRef<string | null>(null);
  const handleSubToggle = (sub: string) => {
    setSelectedSubs(prev => {
      const next = prev.includes(sub) ? prev.filter(x => x !== sub) : [...prev, sub];
      // If adding a sub and no regions selected, auto-select the parent region
      if (!prev.includes(sub) && selectedRegions.length === 0) {
        const parent = subToRegion.get(sub);
        if (parent) {
          setSelectedRegions([parent]);
          autoSelectedRegionRef.current = parent;
        }
      }
      // If removing a sub and an auto-selected region has no remaining subs, clear it
      if (prev.includes(sub) && autoSelectedRegionRef.current) {
        const regionSubs = regionToSubs.get(autoSelectedRegionRef.current);
        const hasRemaining = next.some(s => regionSubs?.has(s));
        if (!hasRemaining) {
          setSelectedRegions([]);
          autoSelectedRegionRef.current = null;
        }
      }
      return next;
    });
  };

  // Clean up selectedSubs when region filter removes them
  const effectiveSubs = selectedSubs.filter(s => availableSubs.includes(s));
  const allCloseQs = [...new Set(mergedDeals.map(d => d.close_q).filter(Boolean))].sort();
  const mergedFiltered = mergedDeals.filter(d => {
    if (searchId && !d.id.toLowerCase().includes(searchId.toLowerCase())) return false;
    if (searchAccount && !d.account.toLowerCase().includes(searchAccount.toLowerCase())) return false;
    if (searchSc && !d.mergedSc.toLowerCase().includes(searchSc.toLowerCase())) return false;
    if (selectedStatuses.length > 0 && !selectedStatuses.includes(d.displayStatus)) return false;
    if (selectedRegions.length > 0 && !selectedRegions.includes(d.region)) return false;
    if (effectiveSubs.length > 0 && !effectiveSubs.includes(d.sub_region)) return false;
    if (d.age !== null && (d.age < ageMin || d.age > ageMax)) return false;
    if (d.gnarr !== null && (d.gnarr < gnarrMin || d.gnarr > gnarrMax)) return false;
    if (selectedCloseQs.length > 0 && !selectedCloseQs.includes(d.close_q)) return false;
    if (selectedSolution) {
      const dealSolution = bucketSolution(d.products || '');
      if (selectedSolution === 'Others') {
        if (!othersLabels.includes(dealSolution)) return false;
      } else if (dealSolution !== selectedSolution) {
        return false;
      }
    }
    return true;
  });
  const STAGE_COLORS: Record<string, {
    bg: string;
    fg: string;
  }> = {
    'Prospect': {
      bg: '#F3F4F6',
      fg: '#6B7280'
    },
    'Qualification': {
      bg: '#EFF6FF',
      fg: '#3B82F6'
    },
    'Influence': {
      bg: '#F0F9FF',
      fg: '#0EA5E9'
    },
    'Solutioning': {
      bg: '#F5F3FF',
      fg: '#8B5CF6'
    },
    'Customer Commit': {
      bg: '#FFF7ED',
      fg: '#F97316'
    },
    'Order': {
      bg: '#FFFBEB',
      fg: '#D97706'
    },
    'Closed - Booked': {
      bg: '#F0FDF4',
      fg: '#16A34A'
    }
  };
  const statusBadge = (status: string) => {
    const c = STAGE_COLORS[status] || {
      bg: '#F9FAFB',
      fg: '#374151'
    };
    return <span className="badge" style={{
      background: c.bg,
      color: c.fg,
      fontSize: 8
    }} data-id="element-593" data-mp="9drsndc9spze">{status}</span>;
  };
  const mergedColumns: SortableColumn<MergedDeal>[] = [{
    key: 'id',
    label: 'DR ID',
    width: '8%',
    sortValue: d => d.id,
    render: d => <span style={{
      fontSize: 9,
      color: '#8892a0'
    }} data-id="element-594" data-mp="1adpbsctpfh8b">{d.id}</span>
  }, {
    key: 'account',
    label: 'Account',
    width: '18%',
    sortValue: d => d.account,
    render: d => <span title={d.account} data-id="element-595" data-mp="1eglq3rc7or6g">{d.account.substring(0, 24)}</span>
  }, {
    key: 'mergedSc',
    label: 'SC Assigned',
    width: '14%',
    sortValue: d => d.mergedSc,
    render: d => <span title={d.mergedSc} data-id="element-596" data-mp="1odhzdz9xphc2">{d.mergedSc.split(',').slice(0, 2).join(', ')}</span>
  }, {
    key: 'displayStatus',
    label: 'Status',
    width: '9%',
    sortValue: d => d.displayStatus,
    render: d => statusBadge(d.displayStatus)
  }, {
    key: 'offering',
    label: 'Offering',
    width: '12%',
    sortValue: d => d.offering,
    render: d => <span className="badge b-blue" style={{
      fontSize: 8
    }} data-id="element-597" data-mp="224ven10t7t1a">
        {d.offering.replace('Engagement', 'Eng.').replace('Support', 'Sup.')}
      </span>
  }, {
    key: 'age',
    label: 'Age',
    width: '6%',
    sortValue: d => d.age ?? 0,
    render: d => <span className={ageSeverity(d.age) === 'high' ? 'c-red' : ageSeverity(d.age) === 'medium' ? 'c-gold' : ''} style={{
      fontWeight: ageSeverity(d.age) === 'high' ? 600 : 400
    }} data-id="element-598" data-mp="2h0un4crtzv6y">
        {formatAge(d.age)}
      </span>
  }, {
    key: 'gnarr',
    label: 'GNARR',
    width: '9%',
    sortValue: d => d.gnarr,
    render: d => <>{fmtG(d.gnarr)}</>
  }, {
    key: 'close_q',
    label: 'Close Q',
    width: '8%',
    sortValue: d => d.close_q,
    render: d => <>{d.close_q}</>
  }, {
    key: 'region',
    label: 'Region',
    width: '7%',
    sortValue: d => d.region,
    render: d => regionBadge(d.region)
  }, {
    key: 'sub_region',
    label: 'Sub',
    width: '5%',
    sortValue: d => d.sub_region,
    render: d => <span className="badge b-gray" style={{
      fontSize: 8
    }} data-id="element-599" data-mp="256pxrkmkwthx">{d.sub_region}</span>
  }];
  const agedColumns: SortableColumn<Deal>[] = [{
    key: 'account',
    label: 'Account',
    width: '30%',
    sortValue: d => d.account,
    render: d => <span title={d.account} data-id="element-600" data-mp="pnlexz52xy5h">{d.account.substring(0, 22)}</span>
  }, {
    key: 'sc',
    label: 'SC',
    width: '20%',
    sortValue: d => d.sc,
    render: d => <span title={d.sc} data-id="element-601" data-mp="kq3e6w17up8k">{d.sc.split(' ')[0]}</span>
  }, {
    key: 'age',
    label: 'Age',
    width: '12%',
    sortValue: d => d.age ?? 0,
    render: d => <span className="c-red" style={{
      fontWeight: 600
    }} data-id="element-602" data-mp="2wehoxadrh3bn">{formatAge(d.age)}</span>
  }, {
    key: 'gnarr',
    label: 'GNARR',
    width: '18%',
    sortValue: d => d.gnarr,
    render: d => <>{fmtG(d.gnarr)}</>
  }, {
    key: 'close_q',
    label: 'Close Q',
    width: '12%',
    sortValue: d => d.close_q,
    render: d => <>{d.close_q}</>
  }, {
    key: 'sub_region',
    label: 'Sub',
    width: '8%',
    sortValue: d => d.sub_region,
    render: d => <span className="badge b-gray" style={{
      fontSize: 8
    }} data-id="element-603" data-mp="2do0c6f9bqibx">{d.sub_region}</span>
  }];
  return <div data-id="element-604" data-mp="2hkwdue4w6pgp">
      {/* Metrics */}
      <div className="mrow" style={{
      gridTemplateColumns: 'repeat(5, 1fr)'
    }} data-id="element-605" data-mp="23nzm8agy93yr">
        <MetricCard label="In-progress requests" value={ip.length} sub={`${scs} SCs active`} colorClass="c-blue" data-id="element-606" data-mp="28mj8kbzgghrn" /> 
        <MetricCard label="Closed requests" value={cls.length} colorClass="c-green" data-id="element-607" data-mp="1vlaxu8wuxrb" />
        <MetricCard label="Aged >30 days" value={aged.length} sub="need immediate action" colorClass="c-red" data-id="element-608" data-mp="6uxvg8cb7sqq" />
        <MetricCard label="Closed Booked GNARR" value={fmtG(wonGnarr)} sub={`${wonCount} won opportunit${wonCount === 1 ? 'y' : 'ies'}`} colorClass="c-purple" data-id="element-609" data-mp="22yug4s5jnunv" />
        <GnarrInfluencedCard value={oppGnarr} colorClass="c-green" data-id="element-610" data-mp="1xx51z3n0z0vk" />
      </div>

      {/* Offering chart + Aged deals */}
      <div className="row2" data-id="element-611" data-mp="1oyth7i8uaxb1">
        <div className="card" style={{
        minHeight: 260
      }} data-id="element-612" data-mp="1oxi1ql618143">
          <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 4
        }} data-id="element-613" data-mp="1jzgio08lw1sm">
            <h3 style={{
            margin: 0
          }} data-id="element-614" data-mp="1f902i8dei22k">Requests by solution</h3>
            <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 9,
            color: '#667085'
          }} data-id="element-615" data-mp="1f6ndxcd50tgj">
              <span data-id="element-616" data-mp="1a7arutu3bqf0">Top</span>
              <input type="range" min={1} max={offeringTotal} value={offeringTop} onChange={e => setOfferingTop(Number(e.target.value))} style={{
              width: 80,
              accentColor: '#3B82F6'
            }} data-id="element-617" data-mp="168lidcc6at0n" />
              <span style={{
              minWidth: 20,
              textAlign: 'right'
            }} data-id="element-618" data-mp="wanvas61v29m">{offeringTop}</span>
            </div>
          </div>
          <OfferingChart deals={all} top={offeringTop} onTopChange={setOfferingTop} onTotalChange={setOfferingTotal} selected={selectedSolution} onSelect={handleSolutionClick} onOthersChange={setOthersLabels} data-id="element-619" data-mp="37zc0b6aoa9o7" />
        </div>
        <div className="card" style={{
        minHeight: 260
      }} data-id="element-620" data-mp="2p6q24fcam70g">
          <h3 data-id="element-621" data-mp="2p8ax262irryu">Aged deals (&gt;30 days) at risk</h3>
          {agedTop8.length > 0 ? <SortableTable columns={agedColumns} data={agedTop8} rowKey={d => `${d.id}`} defaultSort={{
          key: 'age',
          dir: 'desc'
        }} data-id="element-622" data-mp="2u6mb6szukomv" /> : <div style={{
          padding: '20px',
          textAlign: 'center',
          color: '#8892a0',
          fontSize: 11
        }} data-id="element-623" data-mp="32zftz0ei665h">No aged deals in this filter ✓</div>}
        </div>
      </div>

      {/* Accounts — full list */}
      <SectionHeader title={<>
          Accounts — full list
          {selectedSolution && <span onClick={() => setSelectedSolution(null)} style={{
        marginLeft: 8,
        fontSize: 9,
        fontWeight: 600,
        color: '#3B82F6',
        cursor: 'pointer',
        textTransform: 'none',
        letterSpacing: 'normal'
      }} data-id="element-625" data-mp="3dqel1xmtk9fd">
              · {selectedSolution} ✕
            </span>}
        </>} data-id="element-624" data-mp="2afqzdky52cos" />
      <div style={{
      padding: '8px 18px 0',
      display: 'flex',
      alignItems: 'center',
      gap: 8
    }} data-id="element-626" data-mp="2uueyx3giw03p">
        <input type="text" placeholder="Search DR ID…" value={searchId} onChange={e => setSearchId(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 120,
        color: '#344054',
        background: '#fff'
      }} data-id="element-627" data-mp="2pwd6vt14cqg3" />
        <input type="text" placeholder="Search Account…" value={searchAccount} onChange={e => setSearchAccount(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 140,
        color: '#344054',
        background: '#fff'
      }} data-id="element-628" data-mp="f3irc14ij92q" />
        <input type="text" placeholder="Search SC…" value={searchSc} onChange={e => setSearchSc(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 140,
        color: '#344054',
        background: '#fff'
      }} data-id="element-629" data-mp="hmx8nnhicc55" />
        <div ref={statusDropdownRef} style={{
        position: 'relative',
        zIndex: statusDropdownOpen ? 10 : undefined
      }} data-id="element-630" data-mp="vkvnetzqqal6">
          <button onClick={() => setStatusDropdownOpen(!statusDropdownOpen)} style={{
          fontSize: 10,
          padding: '4px 8px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }} data-id="element-631" data-mp="1obm11g94756m">
            {selectedStatuses.length > 0 ? `Status (${selectedStatuses.length})` : 'Status ▾'}
          </button>
          {statusDropdownOpen && <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          zIndex: 50,
          background: '#fff',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          boxShadow: '0 2px 6px rgba(0,0,0,.12)',
          minWidth: 160,
          padding: '4px 0'
        }} data-id="element-632" data-mp="3bevbgafzmw8p">
              <div onClick={() => setSelectedStatuses(selectedStatuses.length === allStatuses.length ? [] : [...allStatuses])} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            color: '#667085',
            borderBottom: '1px solid #eaecf0'
          }} data-id="element-633" data-mp="2xr04la6vnco">
                {selectedStatuses.length === allStatuses.length ? 'Deselect all' : 'Select all'}
              </div>
              {allStatuses.map(s => <label key={s} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }} onClick={e => e.stopPropagation()} data-id="element-634" data-mp="28navnqi9vqzz">
                  <input type="checkbox" checked={selectedStatuses.includes(s)} onChange={() => {
              setSelectedStatuses(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
            }} data-id="element-635" data-mp="2nam95xy0ll2q" />
                  {s}
                </label>)}
            </div>}
        </div>
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10,
        color: '#667085'
      }} data-id="element-636" data-mp="366vwzcypxpzx">
          <span data-id="element-637" data-mp="16yguy3i9lrkm">Age</span>
          <RangeSlider min={dataAgeMin} max={dataAgeMax} low={ageMin} high={ageMax} onChangeLow={setAgeMin} onChangeHigh={setAgeMax} width={100} data-id="element-638" data-mp="1bup8xdkq7eq7" />
          <span style={{
          fontSize: 9,
          color: '#8892a0'
        }} data-id="element-639" data-mp="3fx653etkee36">{ageMin}–{ageMax}d</span>
        </div>
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10,
        color: '#667085'
      }} data-id="element-640" data-mp="3jyrntgss5mla">
          <span data-id="element-641" data-mp="3oxul2f2iqoip">GNARR</span>
          <RangeSlider min={dataGnarrMin} max={dataGnarrMax} low={gnarrMin} high={gnarrMax} onChangeLow={setGnarrMin} onChangeHigh={setGnarrMax} width={100} data-id="element-642" data-mp="3tqdxv9tdtfmg" />
          <span style={{
          fontSize: 9,
          color: '#8892a0'
        }} data-id="element-643" data-mp="2mp73l2nd8h1">${(gnarrMin / 1e6).toFixed(0)}M–${(gnarrMax / 1e6).toFixed(0)}M</span>
        </div>
        <div ref={closeQDropdownRef} style={{
        position: 'relative',
        zIndex: closeQDropdownOpen ? 10 : undefined
      }} data-id="element-644" data-mp="bkss8g9q038b">
          <button onClick={() => setCloseQDropdownOpen(!closeQDropdownOpen)} style={{
          fontSize: 10,
          padding: '4px 8px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }} data-id="element-645" data-mp="giubb175c2js">
            {selectedCloseQs.length > 0 ? `Close Q (${selectedCloseQs.length})` : 'Close Q ▾'}
          </button>
          {closeQDropdownOpen && <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          zIndex: 50,
          background: '#fff',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          boxShadow: '0 2px 6px rgba(0,0,0,.12)',
          minWidth: 140,
          padding: '4px 0'
        }} data-id="element-646" data-mp="2103imn3sx22p">
              <div onClick={() => setSelectedCloseQs(selectedCloseQs.length === allCloseQs.length ? [] : [...allCloseQs])} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            color: '#667085',
            borderBottom: '1px solid #eaecf0'
          }} data-id="element-647" data-mp="2osjxkos8sdxs">
                {selectedCloseQs.length === allCloseQs.length ? 'Deselect all' : 'Select all'}
              </div>
              {allCloseQs.map(q => <label key={q} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }} onClick={e => e.stopPropagation()} data-id="element-648" data-mp="15sjdu68dbvwr">
                  <input type="checkbox" checked={selectedCloseQs.includes(q)} onChange={() => {
              setSelectedCloseQs(prev => prev.includes(q) ? prev.filter(x => x !== q) : [...prev, q]);
            }} data-id="element-649" data-mp="vun4njyzrb1a" />
                  {q}
                </label>)}
            </div>}
        </div>
        <div ref={regionDropdownRef} style={{
        position: 'relative',
        zIndex: regionDropdownOpen ? 10 : undefined
      }} data-id="element-650" data-mp="i2p3bvbiujfo">
          <button onClick={() => setRegionDropdownOpen(!regionDropdownOpen)} style={{
          fontSize: 10,
          padding: '4px 8px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }} data-id="element-651" data-mp="450hak5px691">
            {selectedRegions.length > 0 ? `Region (${selectedRegions.length})` : 'Region ▾'}
          </button>
          {regionDropdownOpen && <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          zIndex: 50,
          background: '#fff',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          boxShadow: '0 2px 6px rgba(0,0,0,.12)',
          minWidth: 140,
          padding: '4px 0'
        }} data-id="element-652" data-mp="3pq4225p6kbmw">
              <div onClick={() => setSelectedRegions(selectedRegions.length === allRegions.length ? [] : [...allRegions])} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            color: '#667085',
            borderBottom: '1px solid #eaecf0'
          }} data-id="element-653" data-mp="h7elnwzfy1er">
                {selectedRegions.length === allRegions.length ? 'Deselect all' : 'Select all'}
              </div>
              {allRegions.map(r => <label key={r} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }} onClick={e => e.stopPropagation()} data-id="element-654" data-mp="1aah3s8k6bfo2">
                  <input type="checkbox" checked={selectedRegions.includes(r)} onChange={() => {
              setSelectedRegions(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
            }} data-id="element-655" data-mp="1k00nfgfpndjz" />
                  {r}
                </label>)}
            </div>}
        </div>
        <div ref={subDropdownRef} style={{
        position: 'relative',
        zIndex: subDropdownOpen ? 10 : undefined
      }} data-id="element-656" data-mp="27r61bq23bvrn">
          <button onClick={() => setSubDropdownOpen(!subDropdownOpen)} style={{
          fontSize: 10,
          padding: '4px 8px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }} data-id="element-657" data-mp="2coepc79x6u0t">
            {effectiveSubs.length > 0 ? `Sub (${effectiveSubs.length})` : 'Sub ▾'}
          </button>
          {subDropdownOpen && <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          zIndex: 50,
          background: '#fff',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          boxShadow: '0 2px 6px rgba(0,0,0,.12)',
          minWidth: 140,
          padding: '4px 0'
        }} data-id="element-658" data-mp="3ehs9xjszj5eh">
              <div onClick={() => setSelectedSubs(effectiveSubs.length === availableSubs.length ? [] : [...availableSubs])} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            color: '#667085',
            borderBottom: '1px solid #eaecf0'
          }} data-id="element-659" data-mp="2zl1jw157b61j">
                {effectiveSubs.length === availableSubs.length ? 'Deselect all' : 'Select all'}
              </div>
              {availableSubs.map(s => <label key={s} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }} onClick={e => e.stopPropagation()} data-id="element-660" data-mp="2iafj1het6j6z">
                  <input type="checkbox" checked={effectiveSubs.includes(s)} onChange={() => handleSubToggle(s)} data-id="element-661" data-mp="3b0ed5r7v16ii" />
                  {s}
                </label>)}
            </div>}
        </div>
      </div>
      <div className="tw" data-id="element-662" data-mp="35yf7ubsk7zix">
        {searchRows ? <table className="tbl" data-id="element-663" data-mp="1qjhpkb4maukg">
            <thead data-id="element-664" data-mp="1qigfa8vfec3s">
              <tr data-id="element-665" data-mp="1qhy9akiyojio">
                {mergedColumns.map(col => <th key={col.key} style={{
              width: col.width
            }} data-id="element-666" data-mp="1ulwad0ruk4ed">{col.label}</th>)}
              </tr>
            </thead>
            <tbody data-id="element-667" data-mp="1mzhdfwqd1bvp">{searchRows}</tbody>
          </table> : <SortableTable columns={mergedColumns} data={mergedFiltered} rowKey={d => d.id} data-id="element-668" data-mp="1i17hvl0q499k" />}
      </div>

    </div>;
}